import { SearchOutlined } from '@ant-design/icons';
import { Button, Empty, Input, Table } from 'antd';
import { ColumnsType } from 'antd/es/table';
import cn from 'classnames';
import { ChangeEvent, FC, useMemo, useState } from 'react';
import { FormattedMessage, useIntl } from 'react-intl';
import { generatePath, Link } from 'react-router';

import { Organization } from 'protocol/model/dto_organization_pb';

import { PATHS } from '@/shared/config';
import { useDebounce } from '@/shared/hooks';

import {
  organizationsMessages,
  useOrganizationListQuery,
} from '@/entities/organizations';

import {
  organizationClientsEmptyMessages,
  OrganizationClientsKind,
} from '../../config';
import { buildOrganizationClientsFilter } from '../../lib';

import styles from './OrganizationClients.module.scss';

type OrganizationClientsProps = {
  kind: OrganizationClientsKind;
  ownerEmail: string;
  className?: string;
};

type OrganizationClientRow = {
  id: string;
  key: string;
  name: string;
  ownerEmail: string;
  createdAt?: Date;
};

const toRow = (organization: Organization): OrganizationClientRow => {
  const createdSeconds = organization.Created?.At?.seconds;

  return {
    id: organization.ID,
    key: organization.ID,
    name: organization.Name,
    ownerEmail: organization.OwnerEmail,
    createdAt: createdSeconds
      ? new Date(Number(createdSeconds) * 1000)
      : undefined,
  };
};

export const OrganizationClients: FC<OrganizationClientsProps> = (props) => {
  const { kind, ownerEmail, className } = props;

  const { formatDate, formatMessage } = useIntl();

  const [search, setSearch] = useState('');

  const debouncedSearch = useDebounce(search);

  const filter = useMemo(
    () => buildOrganizationClientsFilter({ kind, ownerEmail }),
    [kind, ownerEmail],
  );

  const { hasMore, loading, organizations, loadMore } =
    useOrganizationListQuery({
      enabled: Boolean(ownerEmail),
      filter,
      search: debouncedSearch,
    });

  const rows = useMemo(() => organizations.map(toRow), [organizations]);

  const columns: ColumnsType<OrganizationClientRow> = [
    {
      key: 'name',
      title: <FormattedMessage id="organizations.name" defaultMessage="Name" />,
      render: (_, row) => row.name || '—',
    },
    {
      key: 'email',
      title: (
        <FormattedMessage id="organizations.email" defaultMessage="E-mail" />
      ),
      render: (_, row) => row.ownerEmail || '—',
    },
    {
      key: 'created',
      title: <FormattedMessage id="global.created" defaultMessage="Created" />,
      render: (_, row) =>
        row.createdAt
          ? formatDate(row.createdAt, {
              year: 'numeric',
              month: 'long',
              day: '2-digit',
            })
          : '—',
    },
    {
      key: 'action',
      title: (
        <FormattedMessage id="organizations.action" defaultMessage="Action" />
      ),
      width: 1,
      render: (_, row) => (
        <Link
          className={styles.detailsLink}
          to={generatePath(PATHS.organization, { organizationID: row.id })}
        >
          <FormattedMessage
            id="organizations.detailsLink"
            defaultMessage="Details"
          />
        </Link>
      ),
    },
  ];

  const handleSearchChange = (event: ChangeEvent<HTMLInputElement>) => {
    setSearch(event.currentTarget.value);
  };

  return (
    <div className={cn(styles.container, className)}>
      <Input
        className={styles.search}
        value={search}
        placeholder={formatMessage(organizationsMessages.searchPlaceholder)}
        prefix={<SearchOutlined rev={undefined} />}
        allowClear
        onChange={handleSearchChange}
      />

      <Table<OrganizationClientRow>
        columns={columns}
        dataSource={rows}
        loading={loading}
        pagination={false}
        locale={{
          emptyText: (
            <Empty
              image={Empty.PRESENTED_IMAGE_SIMPLE}
              description={formatMessage(
                organizationClientsEmptyMessages[kind],
              )}
            />
          ),
        }}
      />

      {hasMore && (
        <Button type="text" onClick={loadMore} loading={loading}>
          <FormattedMessage id="global.showMore" defaultMessage="Show more" />
        </Button>
      )}
    </div>
  );
};
